// USER LAYERS — text/image elements the END USER adds on top of a template
// (docs/user-layers-plan.md, Part B). Pure module: no react, no context, so the
// reducer, the merge and the draft validation are all unit-testable and the
// export path (useImageProcessor) can call the merge outside a hook.
//
// Three stores hold one layer between them, keyed by the SAME id:
//   userLayersByDesign[designKey]  the layer record (kind, z-slot, style)
//   selections[layer.id]           the text string / the picked ImageValue
//   sceneTransforms[layer.id]      the geometry the user dragged it to
// The id doubles as the scene element id AND the inputKey, which is what makes
// every existing inputKey-driven surface (markers, inline edit, transforms,
// imageFit, the export payload) work on a layer with no special-casing.

import {LATIN_FALLBACK_FAMILY, isUserLayerId, USER_LAYER_ID_PREFIX} from './sceneToKonva';
import {ImageFitMap} from './imageFit';
import {Scene, SceneElement, SceneValues, SceneTransforms, ElementTransform} from './types';

export {isUserLayerId, USER_LAYER_ID_PREFIX};

export type LayerAlign = 'left' | 'center' | 'right';

export interface UserLayerStyle {
    /** ABSENT = inherit the reference text element's colour at merge time. The
     *  spec's default is "the colour of the element we cloned", which a stored
     *  '#FFFFFF' would silently override — so "not chosen" has to stay
     *  representable rather than being eagerly resolved at creation. */
    color?: string;
    /** ABSENT = DEFAULT_TEXT_ALIGN. */
    align?: LayerAlign;
}

export interface UserLayer {
    /** 'ul_' + random. Also the scene element id, the inputKey and the
     *  selections/sceneTransforms key. Unique across ALL designs, because
     *  `selections` is one flat map shared by every design. */
    id: string;
    kind: 'text' | 'image';
    /** Insertion slot in scene.elements: an ABSOLUTE index, 0 = below every
     *  template element, scene.elements.length = above all of them. Safe as an
     *  absolute index because a draft saved behind the template's
     *  contentRevision is discarded on hydration, so a Figma re-sync can never
     *  rebase a stale order. */
    order: number;
    /** Text layers only. */
    style?: UserLayerStyle;
}

export type UserLayersByDesign = { [designKey: string]: UserLayer[] };

/** Per design. Ten is already far past "a caption and a logo"; the cap exists so
 *  a runaway tap can't grow the draft payload or the render past reason. */
export const MAX_USER_LAYERS_PER_DESIGN = 10;

/** design.key is the immutable identifier; the index is the last resort for a
 *  pre-key design (same fallback the generation provenance uses). */
/** A bare String(index) fallback could collide with a real design whose key
 *  literally IS "1", silently merging two designs' layers; the prefix cannot
 *  appear in a Figma-derived key. No stored draft predates this. */
export const INDEX_DESIGN_KEY_PREFIX = '__index__:';

export const designKeyOf = (design: { key?: string } | null | undefined, designIndex: number | undefined): string =>
    design?.key || `${INDEX_DESIGN_KEY_PREFIX}${designIndex ?? 0}`;

/** Bounded on purpose: the id is echoed into element ids, selection keys and
 *  transform keys, all of which end up in a payload the export renderer reads. */
export const USER_LAYER_ID_RE = /^ul_[A-Za-z0-9_-]{4,32}$/;

/** `crypto.randomUUID` isn't available in every browser this app targets (and
 *  jsdom needs a shim), and there is no nanoid/uuid dependency — so a short
 *  random token with a timestamp prefix. Collision-free enough for at most ten
 *  ids per design in one browser tab. */
export const newUserLayerId = (): string =>
    `${USER_LAYER_ID_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// Z-ORDER SLOTS
// ---------------------------------------------------------------------------

/** The importer emits a solid/gradient tint stacked over an image fill as a
 *  sibling rect id'd `<imageId>__overlay<i>` (extractor._append_image_overlays).
 *  A layer must never land BETWEEN a photo and its tint, so the slot right
 *  before such a rect is not a legal insertion point. */
const isOverlayContinuation = (el: SceneElement | undefined): boolean =>
    !!el && /__overlay\d+$/.test(el.id || '');

/** Rounding noise from the importer: a "full-board" element is routinely
 *  0.0001px off the canvas edge. */
const EDGE_EPS = 0.5;

/** Does this element cover the ENTIRE canvas AND paint over everything below
 *  it? Editability is irrelevant. A text box that happens to span the board or
 *  a fully transparent element hides nothing, so neither raises the floor. */
const isFullBoard = (el: SceneElement | undefined, w: number, h: number): boolean =>
    !!el && el.type !== 'text' && (el.opacity ?? 1) > 0
    && (el.x ?? 0) <= EDGE_EPS && (el.y ?? 0) <= EDGE_EPS
    && (el.w ?? 0) >= w - EDGE_EPS && (el.h ?? 0) >= h - EDGE_EPS;

/**
 * The LOWEST slot a user layer may occupy: just above the leading run of
 * full-board elements (the design's backdrop, plus any second backdrop or tint
 * stacked straight onto it).
 *
 * Without this, "send to back" put the layer under an OPAQUE backdrop and the
 * layer simply vanished — indistinguishable from having been deleted, which is
 * the one thing a z-order control must never look like. A design with no
 * leading full-board element has a floor of 0, exactly as before.
 */
export const fullBoardFloor = (scene: Scene | null | undefined): number => {
    const elements = scene?.elements ?? [];
    const w = scene?.width ?? 0;
    const h = scene?.height ?? 0;
    if (!(w > 0) || !(h > 0)) return 0;
    let i = 0;
    while (i < elements.length && isFullBoard(elements[i], w, h)) i++;
    return i;
};

/** Legal `order` values for this scene, ascending. The first entry is the
 *  full-board floor (0 for a design with no backdrop); the last is
 *  scene.elements.length (the very top). Slots that would split an
 *  image+overlay unit are omitted. */
export const validSlots = (scene: Scene | null | undefined): number[] => {
    const elements = scene?.elements ?? [];
    const floor = fullBoardFloor(scene);
    const slots: number[] = [];
    for (let i = floor; i < elements.length; i++) {
        // The floor itself is always legal; above it, never split a unit.
        if (i === floor || !isOverlayContinuation(elements[i])) slots.push(i);
    }
    if (slots[slots.length - 1] !== elements.length) slots.push(elements.length);
    return slots;
};

/** The nearest legal slot at or below `order`, clamped into range. A restored
 *  order that would split an image+overlay unit is snapped DOWN rather than
 *  dropped: the layer's z is approximate anyway, its existence is not. An order
 *  BELOW the full-board floor (a draft written before the floor existed) snaps
 *  UP to it, so the layer comes back visible instead of hidden by the backdrop. */
export const snapOrderToSlot = (order: number, scene: Scene | null | undefined): number => {
    const slots = validSlots(scene);
    const clamped = Math.min(Math.max(0, Math.floor(order)), slots[slots.length - 1]);
    let best = slots[0];
    for (const slot of slots) if (slot <= clamped) best = slot;
    return best;
};

const slotAbove = (slots: number[], order: number): number | null => {
    for (const s of slots) if (s > order) return s;
    return null;
};

const slotBelow = (slots: number[], order: number): number | null => {
    for (let i = slots.length - 1; i >= 0; i--) if (slots[i] < order) return slots[i];
    return null;
};

/** Layers are kept sorted by (order, insertion position); ties are therefore
 *  contiguous, so "is there another layer stacked directly above me" is just a
 *  look at the neighbour. */
export const canMoveLayer = (
    layers: UserLayer[] | undefined,
    id: string,
    direction: 'up' | 'down',
    slots: number[],
): boolean => {
    const list = layers ?? [];
    const index = list.findIndex((l) => l.id === id);
    if (index < 0) return false;
    const layer = list[index];
    if (direction === 'up') {
        const above = list[index + 1];
        return (!!above && above.order === layer.order) || slotAbove(slots, layer.order) !== null;
    }
    const below = list[index - 1];
    return (!!below && below.order === layer.order) || slotBelow(slots, layer.order) !== null;
};

// ---------------------------------------------------------------------------
// REDUCER
// ---------------------------------------------------------------------------

export type UserLayersAction =
    | { type: 'reset' }
    | { type: 'hydrate'; value: UserLayersByDesign }
    | { type: 'add'; designKey: string; layer: UserLayer }
    | { type: 'remove'; designKey: string; id: string }
    /** Re-insert a removed layer at its previous array position (delete-undo). */
    | { type: 'restore'; designKey: string; layer: UserLayer; index: number }
    /** `slots` comes from validSlots(scene) — passed in so the reducer stays
     *  pure and scene-free. */
    | { type: 'move'; designKey: string; id: string; move: 'up' | 'down' | 'front' | 'back'; slots: number[] }
    | { type: 'setStyle'; designKey: string; id: string; style: Partial<UserLayerStyle> };

/** Insert `layer` into an already-sorted list. `edge` decides which end of an
 *  equal-order run it joins: 'bottom' for a layer arriving from below (it must
 *  stay under the ones already there), 'top' for one arriving from above. */
const insertSorted = (list: UserLayer[], layer: UserLayer, edge: 'top' | 'bottom'): UserLayer[] => {
    const out = [...list];
    let i = 0;
    while (i < out.length && out[i].order < layer.order) i++;
    if (edge === 'top') {
        while (i < out.length && out[i].order === layer.order) i++;
    }
    out.splice(i, 0, layer);
    return out;
};

export const userLayersReducer = (
    state: UserLayersByDesign,
    action: UserLayersAction,
): UserLayersByDesign => {
    switch (action.type) {
        case 'reset':
            return {};
        case 'hydrate':
            return action.value;
        case 'add': {
            const list = state[action.designKey] ?? [];
            if (list.length >= MAX_USER_LAYERS_PER_DESIGN) return state;
            if (list.some((l) => l.id === action.layer.id)) return state;
            return {...state, [action.designKey]: insertSorted(list, action.layer, 'top')};
        }
        case 'restore': {
            const list = state[action.designKey] ?? [];
            if (list.length >= MAX_USER_LAYERS_PER_DESIGN) return state;
            if (list.some((l) => l.id === action.layer.id)) return state;
            const next = [...list];
            next.splice(Math.min(Math.max(0, action.index), next.length), 0, action.layer);
            return {...state, [action.designKey]: next};
        }
        case 'remove': {
            const list = state[action.designKey];
            if (!list) return state;
            const next = list.filter((l) => l.id !== action.id);
            if (next.length === list.length) return state;
            return {...state, [action.designKey]: next};
        }
        case 'move': {
            const list = state[action.designKey];
            if (!list) return state;
            const index = list.findIndex((l) => l.id === action.id);
            if (index < 0) return state;
            const layer = list[index];
            const slots = action.slots.length ? action.slots : [0];
            const rest = list.filter((_, i) => i !== index);

            if (action.move === 'front') {
                const top = slots[slots.length - 1];
                if (layer.order === top && index === list.length - 1) return state;
                return {...state, [action.designKey]: insertSorted(rest, {...layer, order: top}, 'top')};
            }
            if (action.move === 'back') {
                const bottom = slots[0];
                if (layer.order === bottom && index === 0) return state;
                return {...state, [action.designKey]: insertSorted(rest, {...layer, order: bottom}, 'bottom')};
            }
            if (action.move === 'up') {
                const above = list[index + 1];
                if (above && above.order === layer.order) {
                    const next = [...list];
                    next[index] = above;
                    next[index + 1] = layer;
                    return {...state, [action.designKey]: next};
                }
                const target = slotAbove(slots, layer.order);
                if (target === null) return state;
                return {...state, [action.designKey]: insertSorted(rest, {...layer, order: target}, 'bottom')};
            }
            // down
            const below = list[index - 1];
            if (below && below.order === layer.order) {
                const next = [...list];
                next[index] = below;
                next[index - 1] = layer;
                return {...state, [action.designKey]: next};
            }
            const target = slotBelow(slots, layer.order);
            if (target === null) return state;
            return {...state, [action.designKey]: insertSorted(rest, {...layer, order: target}, 'top')};
        }
        case 'setStyle': {
            const list = state[action.designKey];
            if (!list) return state;
            let changed = false;
            const next = list.map((l) => {
                if (l.id !== action.id) return l;
                changed = true;
                // Shallow-merge ONLY what was set. Spreading defaults here would
                // stamp '#FFFFFF' onto a layer that is still inheriting.
                return {...l, style: {...l.style, ...action.style}};
            });
            return changed ? {...state, [action.designKey]: next} : state;
        }
        default:
            return state;
    }
};

export const DEFAULT_TEXT_ALIGN: LayerAlign = 'center';
/** Only when there is no reference text element to inherit from. */
export const LAST_RESORT_TEXT_COLOR = '#FFFFFF';

// ---------------------------------------------------------------------------
// MERGE (scene IR + layers -> the scene everything renders from)
// ---------------------------------------------------------------------------

/** Depth-first: the first EDITABLE text element, else the first text element of
 *  any kind. Its typography is what a new text layer inherits, so a layer looks
 *  like it belongs to the design instead of like a browser default. */
const findTextReference = (elements: SceneElement[] | undefined): SceneElement | undefined => {
    let anyText: SceneElement | undefined;
    // Explicit return type: the recursive call below makes it un-inferable (TS7023).
    const walk = (list: SceneElement[] | undefined): SceneElement | undefined => {
        for (const el of list ?? []) {
            if (el?.type === 'text' && el.text) {
                if (el.editable) return el;
                if (!anyText) anyText = el;
            }
            const nested = walk((el as any)?.elements);
            if (nested) return nested;
        }
        return undefined;
    };
    return walk(elements) ?? anyText;
};

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Default font size for a new text layer: 5% of the board height reads as a
 *  caption on a story and a headline on a banner. */
export const defaultLayerFontSize = (scene: Scene): number => Math.max(8, Math.round(scene.height * 0.05));

/**
 * The box a layer STARTS in. Called once at creation (committed straight into
 * sceneTransforms) and again by the merge as the fallback for a layer with no
 * committed transform. Committing it matters for image layers: deriving the box
 * from the asset ratio on every merge would make the box JUMP whenever the user
 * replaces the picture, which is not what "replace this image" should do.
 */
export const initialLayerBox = (
    scene: Scene,
    kind: UserLayer['kind'],
    value?: SceneValues[string],
): { x: number; y: number; w: number; h: number } => {
    if (kind === 'text') {
        const fontSize = defaultLayerFontSize(scene);
        const w = scene.width * 0.6;
        const h = fontSize * 1.4;
        return {x: (scene.width - w) / 2, y: (scene.height - h) / 2, w, h};
    }
    const image = value && typeof value === 'object' ? value : null;
    const w = scene.width * 0.4;
    const ratio = image && finite(image.width) && finite(image.height) && image.width > 0 && image.height > 0
        ? image.height / image.width
        : 1;
    const h = w * ratio;
    return {x: (scene.width - w) / 2, y: (scene.height - h) / 2, w, h};
};

const buildTextLayerElement = (layer: UserLayer, scene: Scene): SceneElement => {
    const ref = findTextReference(scene.elements);
    const refText = ref?.text;
    // 5% of canvas height reads as a caption on a story and a headline on a
    // banner — proportional beats any absolute default here.
    const fontSize = defaultLayerFontSize(scene);
    const ratio = refText && refText.lineHeightPx && refText.fontSize
        ? refText.lineHeightPx / refText.fontSize
        : 1.2;
    const box = initialLayerBox(scene, 'text');
    const refColor = refText?.fill?.type === 'solid' ? refText.fill.color : undefined;
    return {
        id: layer.id,
        name: layer.id,
        type: 'text',
        x: box.x,
        y: box.y,
        w: box.w,
        h: box.h,
        rotation: 0,
        opacity: 1,
        editable: true,
        inputKey: layer.id,
        text: {
            // The real string lives in selections[layer.id]; this is only the
            // fallback for a layer whose value was never written.
            content: '',
            // MUST be a key of template.fonts (the lambda registers exactly
            // those) or the bundled Latin fallback.
            fontPostScriptName: refText?.fontPostScriptName || LATIN_FALLBACK_FAMILY,
            fontSize,
            fontWeight: refText?.fontWeight ?? 400,
            // The user's own pick first, then the element this layer was cloned
            // from, and only then white.
            fill: {type: 'solid', color: layer.style?.color || refColor || LAST_RESORT_TEXT_COLOR},
            align: layer.style?.align || DEFAULT_TEXT_ALIGN,
            verticalAlign: 'top',
            lineHeightPx: fontSize * ratio,
            letterSpacing: refText?.letterSpacing ?? 0,
        },
    };
};

const buildImageLayerElement = (layer: UserLayer, scene: Scene, value: SceneValues[string]): SceneElement | null => {
    const image = value && typeof value === 'object' ? value : null;
    // No asset, no element: a source-less image hard-fails the export renderer
    // (renderSceneNode loadImage strict), so a placeholder must never exist.
    if (!image?.highRes) return null;
    const box = initialLayerBox(scene, 'image', value);
    return {
        id: layer.id,
        name: layer.id,
        type: 'image',
        x: box.x,
        y: box.y,
        w: box.w,
        h: box.h,
        rotation: 0,
        opacity: 1,
        editable: true,
        inputKey: layer.id,
        // 'contain' in the imageFit map (see layerImageFit) overrides this at
        // draw time, so nothing the user picked is cropped; FILL is only what
        // the element would do without that entry.
        image: {scaleMode: 'FILL', src: image.highRes},
    };
};

/**
 * The ONE place a renderable scene is built from a template scene + user layers.
 * Returns the SAME object when the feature is off or there is nothing to add —
 * SceneRenderer resets its load accounting and re-preloads fonts on every new
 * scene object, so a fresh copy per render would thrash the canvas.
 */
export const mergeUserLayers = (
    scene: Scene | undefined | null,
    layers: UserLayer[] | undefined,
    values: SceneValues,
    enabled: boolean,
): Scene | undefined => {
    if (!scene) return scene ?? undefined;
    if (!enabled || !layers || layers.length === 0) return scene;

    const built = layers
        .map((layer) => ({
            layer,
            element: layer.kind === 'text'
                ? buildTextLayerElement(layer, scene)
                : buildImageLayerElement(layer, scene, values[layer.id]),
        }))
        .filter((entry): entry is {layer: UserLayer; element: SceneElement} => !!entry.element);

    if (!built.length) return scene;

    const total = scene.elements.length;
    // Clamp to the full-board floor, not to 0: a layer under an opaque backdrop
    // is invisible, and the merge is the last gate before the renderer.
    const floor = fullBoardFloor(scene);
    const clamp = (order: number) => Math.min(Math.max(floor, Math.floor(order) || 0), total);
    const out: SceneElement[] = [];
    for (let i = 0; i <= total; i++) {
        for (const entry of built) if (clamp(entry.layer.order) === i) out.push(entry.element);
        if (i < total) out.push(scene.elements[i]);
    }
    return {...scene, elements: out};
};

/** Every image layer renders CONTAINED (the Part A box-preserving path), so the
 *  whole picture the user picked is inside the box they sized. Merged over
 *  deriveImageFit's template-field map. */
export const layerImageFit = (
    byDesign: UserLayersByDesign | undefined,
    enabled: boolean,
): ImageFitMap => {
    const out: ImageFitMap = {};
    if (!enabled || !byDesign) return out;
    for (const layers of Object.values(byDesign)) {
        for (const layer of layers ?? []) {
            if (layer.kind === 'image') out[layer.id] = 'contain';
        }
    }
    return out;
};

/** Flat set of every layer id in every design — the valid-key set a restored
 *  draft's selections/sceneTransforms are filtered against. */
export const collectUserLayerIds = (byDesign: UserLayersByDesign | undefined): Set<string> => {
    const ids = new Set<string>();
    for (const layers of Object.values(byDesign ?? {})) {
        for (const layer of layers ?? []) ids.add(layer.id);
    }
    return ids;
};

// ---------------------------------------------------------------------------
// DRAFT VALIDATION
// ---------------------------------------------------------------------------

const isAlign = (v: unknown): v is LayerAlign =>
    v === 'left' || v === 'center' || v === 'right';

/** #rgb / #rrggbb / #rrggbbaa. The value is written straight into a canvas fill,
 *  so an arbitrary string here is a (harmless but) unbounded pass-through into
 *  the render payload; keep it to what the renderer actually understands. */
const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** The design a layer belongs to, resolved once so both the layer parse and the
 *  transform sanitizer can bound values against the right board. */
export interface LayerIndexEntry {
    kind: UserLayer['kind'];
    designKey: string;
    scene?: Scene | null;
}
export type LayerIndex = Map<string, LayerIndexEntry>;

interface TemplateLike {
    designs?: { key?: string; scene?: Scene | null }[];
    userLayersEnabled?: boolean;
}

/** id -> {kind, design, scene}. Built from ALREADY-VALIDATED layers. */
export const buildLayerIndex = (
    byDesign: UserLayersByDesign | undefined,
    template: TemplateLike | undefined,
): LayerIndex => {
    const scenes = new Map<string, Scene | null | undefined>();
    (template?.designs ?? []).forEach((d, i) => scenes.set(designKeyOf(d, i), d?.scene));
    const index: LayerIndex = new Map();
    for (const [designKey, layers] of Object.entries(byDesign ?? {})) {
        for (const layer of layers ?? []) {
            index.set(layer.id, {kind: layer.kind, designKey, scene: scenes.get(designKey)});
        }
    }
    return index;
};

/**
 * Parse `userLayersByDesign` off a restored draft. EVERYTHING is hostile input:
 * the payload is a JSON string the client itself wrote, and the template may
 * have changed since (flag turned off, design removed, image asset gone).
 * Anything that doesn't validate is dropped, never repaired — except `order`,
 * which is SNAPPED to a legal slot of that design's scene: a layer whose z is
 * approximate is far better than a layer that vanishes.
 */
export const parseUserLayers = (
    raw: unknown,
    template: TemplateLike | undefined,
    /** The draft's selections. An image layer with no picked asset would render
     *  a source-less image element, which hard-fails the export; a text layer
     *  whose value is an object would render its fallback and confuse the user. */
    selections: SceneValues | undefined,
): UserLayersByDesign => {
    const out: UserLayersByDesign = {};
    if (!template || template.userLayersEnabled !== true) return out;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;

    const scenes = new Map<string, Scene | null | undefined>();
    (template.designs ?? []).forEach((d, i) => scenes.set(designKeyOf(d, i), d?.scene));
    const seenIds = new Set<string>();

    for (const [designKey, value] of Object.entries(raw as Record<string, unknown>)) {
        if (!scenes.has(designKey) || !Array.isArray(value)) continue;
        const scene = scenes.get(designKey);
        const layers: UserLayer[] = [];
        for (const entry of value) {
            if (layers.length >= MAX_USER_LAYERS_PER_DESIGN) break;
            if (!entry || typeof entry !== 'object') continue;
            const {id, kind, order, style} = entry as Record<string, unknown>;
            if (typeof id !== 'string' || !USER_LAYER_ID_RE.test(id) || seenIds.has(id)) continue;
            if (kind !== 'text' && kind !== 'image') continue;
            if (!finite(order) || order < 0) continue;

            const picked = selections?.[id];
            if (kind === 'image') {
                if (!picked || typeof picked !== 'object' || typeof picked.highRes !== 'string' || !picked.highRes) continue;
            } else if (picked !== undefined && typeof picked !== 'string') {
                // A text layer whose value is an object is a corrupt pairing.
                continue;
            }

            // An image layer has no text style; keeping one would resurrect it
            // if the layer were ever re-kinded.
            const parsedStyle = kind === 'text' && style && typeof style === 'object' && !Array.isArray(style)
                ? (() => {
                    const raw = style as Record<string, unknown>;
                    const clean: UserLayerStyle = {};
                    if (typeof raw.color === 'string' && HEX_COLOR_RE.test(raw.color)) clean.color = raw.color;
                    if (isAlign(raw.align)) clean.align = raw.align;
                    return Object.keys(clean).length ? clean : undefined;
                })()
                : undefined;

            seenIds.add(id);
            layers.push({
                id,
                kind,
                order: snapOrderToSlot(order, scene),
                ...(parsedStyle ? {style: parsedStyle} : {}),
            });
        }
        if (layers.length) {
            // Stored order is the contract; array position only breaks ties.
            out[designKey] = layers.slice().sort((a, b) => a.order - b.order);
        }
    }
    return out;
};

const MIN_FONT_SIZE = 4;
const MAX_FONT_SIZE = 2000;
/** How far off-board a layer may be parked. Generous (a user may push a layer
 *  mostly out of frame on purpose) but finite. */
const OFF_BOARD_FACTOR = 4;

/**
 * A restored transform for a LAYER id. Every present field must be a finite
 * number (a NaN width silently blanks the node) AND inside a sane range: a
 * stored w/h of 0 makes the element invisible-but-selectable, and a 1e9 x parks
 * it where no one can reach it. Text layers additionally lose any rotation —
 * a rotated text can no longer be inline-edited, which is a one-way trap.
 */
export const sanitizeLayerTransform = (
    t: unknown,
    entry?: LayerIndexEntry,
): ElementTransform | null => {
    if (!t || typeof t !== 'object' || Array.isArray(t)) return null;
    const source = t as Record<string, unknown>;
    const out: ElementTransform = {};
    const keys: (keyof ElementTransform)[] = ['x', 'y', 'w', 'h', 'rotation', 'fontSize'];
    for (const key of keys) {
        if (source[key] === undefined) continue;
        if (!finite(source[key])) return null;
        (out as Record<string, number>)[key as string] = source[key] as number;
    }
    if (out.w !== undefined && out.w <= 0) return null;
    if (out.h !== undefined && out.h <= 0) return null;
    if (out.fontSize !== undefined && (out.fontSize < MIN_FONT_SIZE || out.fontSize > MAX_FONT_SIZE)) return null;
    const scene = entry?.scene;
    if (scene) {
        const limitX = scene.width * OFF_BOARD_FACTOR;
        const limitY = scene.height * OFF_BOARD_FACTOR;
        if (out.x !== undefined && Math.abs(out.x) > limitX) return null;
        if (out.y !== undefined && Math.abs(out.y) > limitY) return null;
        if (out.w !== undefined && out.w > limitX) return null;
        if (out.h !== undefined && out.h > limitY) return null;
    }
    if (entry?.kind === 'text') delete out.rotation;
    return out;
};

/** Drop every stored transform whose key is a layer id that no longer exists,
 *  and sanitize the ones that survive. Template-element transforms pass through
 *  untouched (their own id-existence filter runs in TemplateEditor). */
export const sanitizeLayerTransforms = (
    transforms: SceneTransforms,
    layers: LayerIndex | Set<string>,
): SceneTransforms => {
    const index: LayerIndex | null = layers instanceof Map ? layers : null;
    const ids = index ? new Set(index.keys()) : (layers as Set<string>);
    const out: SceneTransforms = {};
    for (const [id, t] of Object.entries(transforms)) {
        if (!isUserLayerId(id)) {
            out[id] = t;
            continue;
        }
        if (!ids.has(id)) continue;
        const clean = sanitizeLayerTransform(t, index?.get(id));
        if (clean) out[id] = clean;
    }
    return out;
};

// ---------------------------------------------------------------------------
// LABELS (shared by the desktop panel and the Fill Mode cards)
// ---------------------------------------------------------------------------

/** How much of a text layer's value a card shows before it is cut. */
export const LAYER_SUBTITLE_CHARS = 24;

/** 1-based position of this layer among the layers of the SAME kind. */
export const sameKindOrdinal = (layers: UserLayer[] | undefined, id: string): number =>
    ((layers ?? []).filter((l) => l.kind === (layers ?? []).find((x) => x.id === id)?.kind)
        .findIndex((l) => l.id === id)) + 1;

/** "טקסט שלי" — suffixed with its number ONLY when the user has more than one
 *  of that kind, so a single layer is not needlessly called "My text 1". */
export const layerTitle = (
    layers: UserLayer[] | undefined,
    layer: UserLayer,
    baseLabel: string,
): string => {
    const sameKind = (layers ?? []).filter((l) => l.kind === layer.kind);
    if (sameKind.length < 2) return baseLabel;
    return `${baseLabel} ${sameKindOrdinal(layers, layer.id)}`;
};

/** The LIVE content line under a layer card's title: the text the user typed,
 *  or the asset they picked. A card that keeps showing the creation-time default
 *  after an edit is worse than no subtitle at all. */
export const layerSubtitle = (
    layer: UserLayer,
    value: string | { name?: string } | undefined,
    emptyTextLabel: string,
    imageFallbackLabel: string,
): string => {
    if (layer.kind === 'image') {
        const name = value && typeof value === 'object' ? (value.name ?? '').trim() : '';
        return name || imageFallbackLabel;
    }
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return emptyTextLabel;
    return text.length > LAYER_SUBTITLE_CHARS ? `${text.slice(0, LAYER_SUBTITLE_CHARS)}…` : text;
};
